import type { Locale } from "@/i18n/locales";

export const AUTH_API_BASE_URL = "https://www.vaccinatieboekje.com/api/v2";

export type AuthProvider = "google" | "facebook" | "apple" | "email";

export type WebAuthUser = {
  id: number;
  firstname: string;
  lastname: string;
  email: string;
  language: Locale;
  type: AuthProvider;
  children?: WebAuthUser[];
  proIsActive?: boolean;
  proExpiresAt?: string | null;
  userCode?: string;
};

export type AuthResponse = {
  token: string;
  user: WebAuthUser;
};

export type CsrfResponse = {
  csrfToken: string;
};

export type LoginPayload =
  | {
      email: string;
      password: string;
    }
  | {
      accessToken: string;
      provider: "google" | "facebook";
    }
  | {
      idToken: string;
      provider: "apple";
    };

export type RegisterPayload = {
  firstname: string;
  lastname: string;
  email: string;
  password: string;
  password_confirmation: string;
  language: Locale;
  captchaToken?: string | null;
};

type RequestOptions = {
  token?: string | null;
  locale?: Locale;
};

function buildHeaders(options: RequestOptions = {}): HeadersInit {
  const headers: Record<string, string> = {
    Accept: "application/json",
    "Content-Type": "application/json",
    "X-Platform": "web",
    "X-Version": "web",
    "X-Language": options.locale ?? "nl",
  };

  if (options.token) {
    headers.Authorization = `Bearer ${options.token}`;
  }

  return headers;
}

async function apiFetch<T>(
  path: string,
  init: RequestInit = {},
  options: RequestOptions = {},
): Promise<T> {
  let response: Response;

  try {
    response = await fetch(`${AUTH_API_BASE_URL}${path}`, {
      ...init,
      credentials: "include",
      headers: {
        ...buildHeaders(options),
        ...(init.headers ?? {}),
      },
    });
  } catch (error) {
    throw error;
  }

  const text = await response.text();
  const body = text ? JSON.parse(text) : null;

  if (!response.ok) {
    const message =
      body?.error ||
      body?.message ||
      response.statusText ||
      "Authentication request failed";
    throw new Error(message);
  }

  return body as T;
}

export function loginWithEmail(email: string, password: string, locale: Locale) {
  return withWebCsrf(locale, (csrfToken) =>
    apiFetch<AuthResponse>(
      "/web/auth/login",
      {
        method: "POST",
        body: JSON.stringify({ email, password }),
        headers: { "X-CSRF-TOKEN": csrfToken },
      },
      { locale },
    ),
  );
}

export function registerWithEmail(payload: RegisterPayload) {
  return withWebCsrf(payload.language, (csrfToken) =>
    apiFetch<AuthResponse>(
      "/web/auth/register",
      {
        method: "POST",
        body: JSON.stringify({
          firstname: payload.firstname,
          lastname: payload.lastname,
          email: payload.email,
          password: payload.password,
          password_confirmation: payload.password_confirmation,
          language: payload.language,
          "g-recaptcha-response": payload.captchaToken ?? undefined,
        }),
        headers: { "X-CSRF-TOKEN": csrfToken },
      },
      { locale: payload.language },
    ),
  );
}

export function loginWithProvider(
  provider: "google" | "facebook",
  accessToken: string,
  locale: Locale,
) {
  return withWebCsrf(locale, (csrfToken) =>
    apiFetch<AuthResponse>(
      "/web/auth/login",
      {
        method: "POST",
        body: JSON.stringify({ accessToken, provider }),
        headers: { "X-CSRF-TOKEN": csrfToken },
      },
      { locale },
    ),
  );
}

export function loginWithApple(idToken: string, locale: Locale) {
  return withWebCsrf(locale, (csrfToken) =>
    apiFetch<AuthResponse>(
      "/web/auth/login",
      {
        method: "POST",
        body: JSON.stringify({ idToken, provider: "apple" }),
        headers: { "X-CSRF-TOKEN": csrfToken },
      },
      { locale },
    ),
  );
}

export function refreshWebSession(locale: Locale) {
  return withWebCsrf(locale, (csrfToken) =>
    apiFetch<AuthResponse>(
      "/web/auth/refresh",
      {
        method: "POST",
        headers: { "X-CSRF-TOKEN": csrfToken },
      },
      { locale },
    ),
  );
}

export function logoutWebSession(locale: Locale, token?: string | null) {
  return withWebCsrf(locale, (csrfToken) =>
    apiFetch<{ success: boolean }>(
      "/web/auth/logout",
      {
        method: "POST",
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          "X-CSRF-TOKEN": csrfToken,
        },
      },
      { locale, token: token ?? null },
    ),
  );
}

export function fetchWebUser(userId: number, token: string, locale: Locale) {
  return apiFetch<WebAuthUser>(`/users/${userId}`, {}, { token, locale });
}

export function fetchWebCsrfToken(locale: Locale) {
  return apiFetch<CsrfResponse>(
    "/web/auth/csrf",
    {
      method: "GET",
    },
    { locale },
  );
}

async function withWebCsrf<T>(
  locale: Locale,
  callback: (csrfToken: string) => Promise<T>,
) {
  const { csrfToken } = await fetchWebCsrfToken(locale);
  return callback(csrfToken);
}

